import { useState, useContext, useMemo } from "react";
import { Box, Text, Flex } from "@chakra-ui/react";
import { LineChart, Line, XAxis, YAxis, ReferenceLine, ResponsiveContainer, Tooltip } from "recharts";
import { CollabDataContext } from "../../../../contexts/collabDataContext";
import { InfluencerCampaignDataContext } from "../../../../contexts/InfluencerCampaignDataContext";
import { useBountyData } from "../../../../contexts/BountyDataContext";
import {
  getFilteredChartData,
  getAllTimeEarnings,
  getTotalBountyEarnings,
  formatEarningsShort,
  TimeRange,
} from "../../../../utils/earningsUtils";
import { ff } from "../../../../theme";

const TIME_RANGES: TimeRange[] = ["1W", "1M", "3M", "YTD", "1Y", "ALL"];

type EarningType = "Cash" | "Credit" | "Both";
const EARNING_TYPES: EarningType[] = ["Both", "Cash", "Credit"];
const EARNING_WORDS: Record<EarningType, string> = { Both: "Cash + credit", Cash: "Cash", Credit: "In-app credit" };

/** Default: ink card. Orders tab: lime-surface card with ink type. */
const PALETTES = {
  default: {
    bg: ff.ink, text: "#FFFFFF", label: ff.faint, amount: ff.lime, sub: ff.faint,
    segBg: ff.inkSoft, segOnBg: ff.lime, segOnText: ff.ink, segOffText: ff.faint,
    line: ff.lime, base: "#2A2A2C", perOnBg: "#2A2A2C", perOnText: "#FFFFFF", perOffText: ff.muted,
  },
  marketplaceOrders: {
    bg: ff.limeSurface, text: ff.ink, label: ff.text2, amount: ff.ink, sub: "#55555A",
    segBg: "#FFFFFF", segOnBg: ff.ink, segOnText: ff.lime, segOffText: ff.text2,
    line: ff.ink, base: ff.border, perOnBg: ff.ink, perOnText: "#FFFFFF", perOffText: ff.text2,
  },
};

export default function DashboardCard(props: { variant?: "default" | "marketplaceOrders" }) {
  const collabData = useContext(CollabDataContext);
  const campaignData = useContext(InfluencerCampaignDataContext);
  const { claims } = useBountyData();
  const [earning, setEarning] = useState<EarningType>("Both");
  const [timeRange, setTimeRange] = useState<TimeRange>("ALL");
  const c = PALETTES[props.variant ?? "default"];

  const earningType = earning === "Cash" ? "cash" : earning === "Credit" ? "credit" : "both";

  const chartData = useMemo(
    () => getFilteredChartData(collabData, campaignData, timeRange, earningType),
    [collabData, campaignData, timeRange, earningType]
  );

  const displayTotal = useMemo(
    () =>
      getAllTimeEarnings(collabData, campaignData, earningType) +
      (earning === "Both" ? getTotalBountyEarnings(claims) : 0),
    [collabData, campaignData, earningType, earning, claims]
  );

  const lastIndex = chartData.length - 1;

  // Whole dollars with separators; past $1M the compact form keeps the figure on one line.
  const totalLabel = displayTotal >= 1_000_000 ? formatEarningsShort(displayTotal) : `$${Math.round(displayTotal).toLocaleString('en-US')}`;

  return (
    <Box
      as="section"
      aria-label="Earnings"
      borderRadius="24px"
      bg={c.bg}
      color={c.text}
      p="20px 18px 10px 18px"
      display="flex"
      flexDirection="column"
      gap="14px"
    >
      <Flex align="flex-start" justify="space-between" gap="12px">
        <Flex direction="column" gap="6px" minW="148px">
          <Text fontSize="11px" fontWeight={600} letterSpacing="0.1em" textTransform="uppercase" color={c.label}>
            {earning === "Cash" ? "Cash Earned" : earning === "Credit" ? "Credits Earned" : "Total Earned"}
          </Text>
          <Text textStyle="display" fontSize={totalLabel.length > 7 ? '44px' : '56px'} textTransform="none" color={c.amount} whiteSpace="nowrap">
            {totalLabel}
          </Text>
          <Text fontSize="13px" color={c.sub}>
            {EARNING_WORDS[earning]} · all time
          </Text>
        </Flex>
        <Flex role="group" aria-label="Earnings type" gap="2px" p="2px" borderRadius="full" bg={c.segBg} flex="none">
          {EARNING_TYPES.map((opt) => {
            const on = opt === earning;
            return (
              <Box
                as="button"
                type="button"
                key={opt}
                aria-pressed={on}
                onClick={() => setEarning(opt)}
                h="36px"
                px="10px"
                borderRadius="full"
                fontSize="12px"
                fontWeight={600}
                bg={on ? c.segOnBg : "transparent"}
                color={on ? c.segOnText : c.segOffText}
              >
                {opt}
              </Box>
            );
          })}
        </Flex>
      </Flex>

      <Box h="96px" role="img" aria-label="Cumulative earnings over the selected period">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 6, right: 6, left: 6, bottom: 6 }}>
            <XAxis dataKey="month" hide />
            <YAxis hide domain={[0, "auto"]} />
            <ReferenceLine y={0} stroke={c.base} strokeWidth={1} />
            <Tooltip
              contentStyle={{
                background: ff.inkSoft,
                border: "none",
                borderRadius: "12px",
                color: "#FFFFFF",
                fontSize: "12px",
                fontWeight: 600,
                fontFamily: "'Archivo', system-ui, sans-serif",
              }}
              itemStyle={{ color: "#FFFFFF" }}
              labelStyle={{ color: ff.faint }}
              formatter={(v) => [`$${Number(v).toFixed(2)}`, earning]}
              cursor={false}
            />
            <Line
              type="linear"
              dataKey="value"
              stroke={c.line}
              strokeWidth={2.5}
              strokeLinecap="round"
              strokeLinejoin="round"
              dot={(p) =>
                p.index === lastIndex ? (
                  <circle key={p.index} cx={p.cx} cy={p.cy} r={5} fill={c.line} />
                ) : (
                  <g key={p.index} />
                )
              }
              activeDot={{ r: 5, fill: c.line, strokeWidth: 0 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </Box>

      <Flex role="group" aria-label="Period" justify="space-between">
        {TIME_RANGES.map((range) => {
          const on = range === timeRange;
          return (
            <Box
              as="button"
              type="button"
              key={range}
              aria-pressed={on}
              onClick={() => setTimeRange(range)}
              flex={1}
              h="44px"
              borderRadius="full"
              fontSize="12px"
              fontWeight={600}
              letterSpacing="0.04em"
              bg={on ? c.perOnBg : "transparent"}
              color={on ? c.perOnText : c.perOffText}
            >
              {range}
            </Box>
          );
        })}
      </Flex>
    </Box>
  );
}
